/**
 * PostingList Component
 * Displays transfer bond requests available for posting with status-based tabs.
 * Matches legacy posting landing page tab structure:
 *   - Available to Quote (with preferred counties toggle)
 *   - In-Progress Transfers (Accepted / Closed / Cancelled sub-tabs)
 *   - Quoted & Declined (Pending Quotes / Declined & Expired sub-tabs)
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  EyeIcon,
  DocumentTextIcon,
  ArrowUpTrayIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  InboxIcon,
  ArrowPathIcon,
  ChatBubbleLeftEllipsisIcon,
} from '@heroicons/react/24/outline';
import {
  getOpenRequestsForAgency,
  getQuotedRequestsForAgency,
  getAcceptedRequestsForAgency,
  getCancelledRequestsForAgency,
  getClosedRequestsForAgency,
  cancelPendingRequest,
  clearDeclinedRequest,
} from '../../../services/oathTransferBondService';
import usePermissions from '../../../hooks/usePermissions';

// ──────────────────────────────────────────────────────────────────────
// Helper functions
// ──────────────────────────────────────────────────────────────────────

const formatCurrency = (value) => {
  if (value == null) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  return (
    d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) +
    ' ' +
    d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
  );
};

const formatTimeRemaining = (expireDate) => {
  if (!expireDate) return '-';
  const now = new Date();
  const expire = new Date(expireDate);
  const diff = expire - now;
  if (diff <= 0) return 'Expired';
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  }
  return `${hours}h ${minutes}m ${seconds}s`;
};

const calculateTotalFees = (row) => {
  const execution = row.executionFee || 0;
  const travel = row.travelFee || 0;
  const misc = row.otherFee || row.miscFee || 0;
  return execution + travel + misc;
};

const getPostingTime = (row) => {
  const mins = row.promisedPostingTimeInMinutes || row.promisedPostingTime || 0;
  return mins ? `${(mins / 60).toFixed(1)}h` : '-';
};

// ──────────────────────────────────────────────────────────────────────
// Hooks
// ──────────────────────────────────────────────────────────────────────

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
};

// ──────────────────────────────────────────────────────────────────────
// Shared UI components
// ──────────────────────────────────────────────────────────────────────

const SortableHeader = ({ label, field, sortField, sortDirection, onSort, align = 'left' }) => {
  const isActive = sortField === field;
  const alignClass = align === 'center' ? 'justify-center' : 'justify-start';
  return (
    <th
      className={`px-4 py-3 text-${align} text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors select-none`}
      onClick={() => onSort(field)}
    >
      <div className={`flex items-center gap-1 ${alignClass}`}>
        <span>{label}</span>
        <span className="text-gray-400 dark:text-gray-500">
          {isActive ? (
            sortDirection === 'asc' ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />
          ) : (
            <ChevronUpDownIcon className="h-4 w-4 opacity-50" />
          )}
        </span>
      </div>
    </th>
  );
};

const MobileLoadingSkeleton = () => (
  <div className="space-y-3">
    {[1, 2, 3].map((i) => (
      <div key={i} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 animate-pulse">
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <div className="h-5 bg-gray-200 dark:bg-gray-600 rounded w-3/4 mb-2" />
            <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-1/2" />
          </div>
          <div className="h-6 bg-gray-200 dark:bg-gray-600 rounded w-20" />
        </div>
        <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          <div><div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-16 mb-1" /><div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-24" /></div>
          <div><div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-16 mb-1" /><div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-20" /></div>
        </div>
      </div>
    ))}
  </div>
);

const TableLoadingSkeleton = () => (
  <div className="animate-pulse">
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="flex items-center gap-4 py-4 border-b border-gray-100 dark:border-gray-700">
        <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-12" />
        <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded flex-1" />
        <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-24" />
        <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-20" />
        <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-16" />
        <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-20" />
      </div>
    ))}
  </div>
);

// ──────────────────────────────────────────────────────────────────────
// Mobile Card Components
// ──────────────────────────────────────────────────────────────────────

const OpenRequestCard = ({ row, onView, onQuote, canViewRequestingAgency }) => {
  const defendantName = `${row.defendantFirstName || ''} ${row.defendantLastName || ''}`.trim() || 'Unknown';
  const location = [row.county, row.state].filter(Boolean).join(', ') || '-';
  const venue = [row.jailLocation, row.courtPlace].filter(Boolean).join(' / ') || '-';
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-md p-4 mb-3 active:bg-gray-50 dark:active:bg-gray-700 transition-colors cursor-pointer" onClick={() => onView(row)}>
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0 pr-2">
          <h3 className="font-semibold text-gray-900 dark:text-white truncate">{defendantName}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Ref #{row.transferBondTransactionID}</p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={(e) => { e.stopPropagation(); onView(row); }} className="p-2 text-primary hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-full transition-colors" aria-label="View details">
            <EyeIcon className="h-5 w-5" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onQuote(row); }} className="p-2 text-success hover:bg-green-50 dark:hover:bg-green-900/20 rounded-full transition-colors" aria-label="Submit quote">
            <DocumentTextIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Location</p><p className="text-sm font-medium text-gray-900 dark:text-white truncate">{location}</p></div>
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Venue</p><p className="text-sm font-medium text-gray-900 dark:text-white truncate">{venue}</p></div>
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Bonds</p><p className="text-sm font-medium text-gray-900 dark:text-white">{row.numberOfBonds || '-'}</p></div>
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Created</p><p className="text-sm font-medium text-gray-900 dark:text-white">{formatDateTime(row.createdAt)}</p></div>
        {canViewRequestingAgency && row.requestingAgencyName && (
          <div className="col-span-2"><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Requesting Agency</p><p className="text-sm font-medium text-gray-900 dark:text-white truncate">{row.requestingAgencyName}</p></div>
        )}
      </div>
    </div>
  );
};

const QuotedRequestCard = ({ row, onView, onCancel, canViewRequestingAgency }) => {
  const defendantName = `${row.defendantFirstName || ''} ${row.defendantLastName || ''}`.trim() || 'Unknown';
  const location = [row.county, row.state].filter(Boolean).join(', ') || '-';
  const venue = [row.jailLocation, row.courtPlace].filter(Boolean).join(' / ') || '-';
  const remaining = formatTimeRemaining(row.expireDate);
  const isExpired = remaining === 'Expired';
  const totalFees = calculateTotalFees(row);
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-md p-4 mb-3 active:bg-gray-50 dark:active:bg-gray-700 transition-colors cursor-pointer" onClick={() => onView(row)}>
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0 pr-2">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-gray-900 dark:text-white truncate">{defendantName}</h3>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${isExpired ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'}`}>
              {isExpired ? 'Expired' : 'Pending'}
            </span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Ref #{row.transferBondTransactionID}</p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={(e) => { e.stopPropagation(); onView(row); }} className="p-2 text-primary hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-full transition-colors" aria-label="View details">
            <EyeIcon className="h-5 w-5" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onCancel(row); }} className="p-2 text-error hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors" aria-label="Cancel quote">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Location</p><p className="text-sm font-medium text-gray-900 dark:text-white truncate">{location}</p></div>
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Venue</p><p className="text-sm font-medium text-gray-900 dark:text-white truncate">{venue}</p></div>
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Bonds</p><p className="text-sm font-medium text-gray-900 dark:text-white">{row.numberOfBonds || '-'}</p></div>
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Total Fees</p><p className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(totalFees)}</p></div>
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Post Time</p><p className="text-sm font-medium text-gray-900 dark:text-white">{getPostingTime(row)}</p></div>
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Expires</p><p className={`text-sm font-medium ${isExpired ? 'text-error' : 'text-gray-900 dark:text-white'}`}>{remaining}</p></div>
        {canViewRequestingAgency && row.requestingAgencyName && (
          <div className="col-span-2"><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Requesting Agency</p><p className="text-sm font-medium text-gray-900 dark:text-white truncate">{row.requestingAgencyName}</p></div>
        )}
      </div>
    </div>
  );
};

const DeclinedQuoteCard = ({ row, onView, onClear, canViewRequestingAgency }) => {
  const defendantName = `${row.defendantFirstName || ''} ${row.defendantLastName || ''}`.trim() || 'Unknown';
  const location = [row.county, row.state].filter(Boolean).join(', ') || '-';
  const totalFees = calculateTotalFees(row);
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-md p-4 mb-3 active:bg-gray-50 dark:active:bg-gray-700 transition-colors cursor-pointer" onClick={() => onView(row)}>
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0 pr-2">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-gray-900 dark:text-white truncate">{defendantName}</h3>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">Declined</span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Ref #{row.transferBondTransactionID}</p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={(e) => { e.stopPropagation(); onView(row); }} className="p-2 text-primary hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-full transition-colors" aria-label="View details">
            <EyeIcon className="h-5 w-5" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onClear(row); }} className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors" aria-label="Clear">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Location</p><p className="text-sm font-medium text-gray-900 dark:text-white truncate">{location}</p></div>
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Total Fees</p><p className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(totalFees)}</p></div>
        {row.declineReason && (
          <div className="col-span-2"><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Decline Reason</p><p className="text-sm font-medium text-gray-900 dark:text-white line-clamp-2">{row.declineReason}</p></div>
        )}
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Date Given</p><p className="text-sm font-medium text-gray-900 dark:text-white">{formatDate(row.dateRecorded)}</p></div>
        {canViewRequestingAgency && row.requestingAgencyName && (
          <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Requesting Agency</p><p className="text-sm font-medium text-gray-900 dark:text-white truncate">{row.requestingAgencyName}</p></div>
        )}
      </div>
    </div>
  );
};

const AcceptedRequestCard = ({ row, onView, onUpload, canViewRequestingAgency }) => {
  const defendantName = `${row.defendantFirstName || ''} ${row.defendantLastName || ''}`.trim() || 'Unknown';
  const location = [row.county, row.state].filter(Boolean).join(', ') || '-';
  const totalFees = calculateTotalFees(row);
  let deadlineText = '-';
  let isOverdue = false;
  if (row.hasBeenPosted) {
    deadlineText = 'Verification Pending';
  } else if (row.dateAccepted && row.promisedPostingTime) {
    const acceptedDate = new Date(row.dateAccepted);
    const deadline = new Date(acceptedDate.getTime() + row.promisedPostingTime * 60 * 1000);
    isOverdue = new Date() > deadline;
    deadlineText = deadline.toLocaleString('en-US', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  }
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-md p-4 mb-3 active:bg-gray-50 dark:active:bg-gray-700 transition-colors cursor-pointer" onClick={() => onView(row)}>
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0 pr-2">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-gray-900 dark:text-white truncate">{defendantName}</h3>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
              row.hasBeenPosted ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' : isOverdue ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
            }`}>
              {row.hasBeenPosted ? 'Verification' : isOverdue ? 'Overdue' : 'Accepted'}
            </span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Ref #{row.transferBondTransactionID}</p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={(e) => { e.stopPropagation(); onView(row); }} className="p-2 text-primary hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-full transition-colors" aria-label="View details">
            <EyeIcon className="h-5 w-5" />
          </button>
          {!row.hasBeenPosted && (
            <button onClick={(e) => { e.stopPropagation(); onUpload(row); }} className="p-2 text-success hover:bg-green-50 dark:hover:bg-green-900/20 rounded-full transition-colors" aria-label="Upload powers">
              <ArrowUpTrayIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
      <div className="mb-3">
        <p className={`text-sm ${isOverdue ? 'text-error' : 'text-gray-600 dark:text-gray-400'}`}>
          {row.hasBeenPosted ? (
            <span className="italic">Awaiting verification...</span>
          ) : (
            <>Post by: {deadlineText} {isOverdue && <span className="text-error">(Overdue)</span>}</>
          )}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Location</p><p className="text-sm font-medium text-gray-900 dark:text-white truncate">{location}</p></div>
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Total Fees</p><p className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(totalFees)}</p></div>
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Bonds</p><p className="text-sm font-medium text-gray-900 dark:text-white">{row.numberOfBonds || '-'}</p></div>
        {canViewRequestingAgency && row.requestingAgencyName && (
          <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Requesting Agency</p><p className="text-sm font-medium text-gray-900 dark:text-white truncate">{row.requestingAgencyName}</p></div>
        )}
      </div>
    </div>
  );
};

const ClosedRequestCard = ({ row, onView, canViewRequestingAgency }) => {
  const defendantName = `${row.defendantFirstName || ''} ${row.defendantLastName || ''}`.trim() || 'Unknown';
  const location = [row.county, row.state].filter(Boolean).join(', ') || '-';
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-md p-4 mb-3 active:bg-gray-50 dark:active:bg-gray-700 transition-colors cursor-pointer" onClick={() => onView(row)}>
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0 pr-2">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-gray-900 dark:text-white truncate">{defendantName}</h3>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">Closed</span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Ref #{row.transferBondTransactionID}</p>
        </div>
        <button onClick={(e) => { e.stopPropagation(); onView(row); }} className="p-2 text-primary hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-full transition-colors" aria-label="View details">
          <EyeIcon className="h-5 w-5" />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Location</p><p className="text-sm font-medium text-gray-900 dark:text-white truncate">{location}</p></div>
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Bond Amount</p><p className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(row.totalBondAmount)}</p></div>
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Created</p><p className="text-sm font-medium text-gray-900 dark:text-white">{formatDateTime(row.createdAt)}</p></div>
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Origin</p><p className="text-sm font-medium text-gray-900 dark:text-white italic">{row.isOnline ? 'Online' : 'Bankers In-House'}</p></div>
        {canViewRequestingAgency && row.requestingAgencyName && (
          <div className="col-span-2"><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Requesting Agency</p><p className="text-sm font-medium text-gray-900 dark:text-white truncate">{row.requestingAgencyName}</p></div>
        )}
      </div>
    </div>
  );
};

const CancelledRequestCard = ({ row, onView, canViewRequestingAgency }) => {
  const defendantName = `${row.defendantFirstName || ''} ${row.defendantLastName || ''}`.trim() || 'Unknown';
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-md p-4 mb-3 active:bg-gray-50 dark:active:bg-gray-700 transition-colors cursor-pointer" onClick={() => onView(row)}>
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0 pr-2">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-gray-900 dark:text-white truncate">{defendantName}</h3>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">Cancelled</span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Ref #{row.transferBondTransactionID}</p>
        </div>
        <button onClick={(e) => { e.stopPropagation(); onView(row); }} className="p-2 text-primary hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-full transition-colors" aria-label="View details">
          <EyeIcon className="h-5 w-5" />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
        {row.cancelledReason && (
          <div className="col-span-2"><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Reason</p><p className="text-sm font-medium text-gray-900 dark:text-white line-clamp-2">{row.cancelledReason}</p></div>
        )}
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Date Cancelled</p><p className="text-sm font-medium text-gray-900 dark:text-white">{formatDateTime(row.cancelledDate)}</p></div>
        {canViewRequestingAgency && row.requestingAgencyName && (
          <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Requesting Agency</p><p className="text-sm font-medium text-gray-900 dark:text-white truncate">{row.requestingAgencyName}</p></div>
        )}
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────
// Desktop Table Row Components
// ──────────────────────────────────────────────────────────────────────

const OpenTableRow = ({ row, onView, onQuote, canViewRequestingAgency }) => {
  const defendantName = `${row.defendantFirstName || ''} ${row.defendantLastName || ''}`.trim() || '-';
  const location = [row.county, row.state].filter(Boolean).join(', ') || '-';
  const venue = [row.jailLocation, row.courtPlace].filter(Boolean).join(' / ') || '-';
  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors" onClick={() => onView(row)}>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">#{row.transferBondTransactionID}</td>
      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white max-w-[200px] truncate">{defendantName}</td>
      {canViewRequestingAgency && <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-[180px] truncate">{row.requestingAgencyName || '-'}</td>}
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-[150px] truncate">{location}</td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-[150px] truncate">{venue}</td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-center">{row.numberOfBonds || '-'}</td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{formatDateTime(row.createdAt)}</td>
      <td className="px-4 py-3 text-sm">
        <div className="flex items-center gap-1">
          <button onClick={(e) => { e.stopPropagation(); onView(row); }} className="p-1.5 text-primary hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors" title="View Details">
            <EyeIcon className="h-5 w-5" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onQuote(row); }} className="p-1.5 text-success hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors" title="Submit Quote">
            <DocumentTextIcon className="h-5 w-5" />
          </button>
        </div>
      </td>
    </tr>
  );
};

const QuotedTableRow = ({ row, onView, onCancel, canViewRequestingAgency }) => {
  const defendantName = `${row.defendantFirstName || ''} ${row.defendantLastName || ''}`.trim() || '-';
  const location = [row.county, row.state].filter(Boolean).join(', ') || '-';
  const remaining = formatTimeRemaining(row.expireDate);
  const isExpired = remaining === 'Expired';
  const totalFees = calculateTotalFees(row);
  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors" onClick={() => onView(row)}>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">#{row.transferBondTransactionID}</td>
      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white max-w-[200px] truncate">{defendantName}</td>
      {canViewRequestingAgency && <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-[180px] truncate">{row.requestingAgencyName || '-'}</td>}
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-[150px] truncate">{location}</td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-[150px] truncate">{row.jailLocation || '-'}</td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-[150px] truncate">{row.courtPlace || '-'}</td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-center">{row.numberOfBonds || '-'}</td>
      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{formatCurrency(totalFees)}</td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-center">{getPostingTime(row)}</td>
      <td className="px-4 py-3 text-sm">
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${isExpired ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'}`}>
          {isExpired ? 'Expired' : 'Pending'}
        </span>
      </td>
      <td className={`px-4 py-3 text-sm ${isExpired ? 'text-error font-medium' : 'text-gray-600 dark:text-gray-400'}`}>{remaining}</td>
      <td className="px-4 py-3 text-sm">
        <div className="flex items-center gap-1">
          <button onClick={(e) => { e.stopPropagation(); onView(row); }} className="p-1.5 text-primary hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors" title="View Details">
            <EyeIcon className="h-5 w-5" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onCancel(row); }} className="p-1.5 text-error hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors" title="Cancel Quote">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </td>
    </tr>
  );
};

const DeclinedTableRow = ({ row, onView, onClear, canViewRequestingAgency }) => {
  const defendantName = `${row.defendantFirstName || ''} ${row.defendantLastName || ''}`.trim() || '-';
  const location = [row.county, row.state].filter(Boolean).join(', ') || '-';
  const totalFees = calculateTotalFees(row);
  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors" onClick={() => onView(row)}>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">#{row.transferBondTransactionID}</td>
      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white max-w-[200px] truncate">{defendantName}</td>
      {canViewRequestingAgency && <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-[180px] truncate">{row.requestingAgencyName || '-'}</td>}
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-[150px] truncate">{location}</td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-[200px] truncate">{row.declineReason || '-'}</td>
      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{formatCurrency(totalFees)}</td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{formatDate(row.dateRecorded)}</td>
      <td className="px-4 py-3 text-sm">
        <div className="flex items-center gap-1">
          <button onClick={(e) => { e.stopPropagation(); onView(row); }} className="p-1.5 text-primary hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors" title="View Details">
            <EyeIcon className="h-5 w-5" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onClear(row); }} className="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors" title="Clear">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </td>
    </tr>
  );
};

const AcceptedTableRow = ({ row, onView, onUpload, canViewRequestingAgency }) => {
  const defendantName = `${row.defendantFirstName || ''} ${row.defendantLastName || ''}`.trim() || '-';
  const location = [row.county, row.state].filter(Boolean).join(', ') || '-';
  const totalFees = calculateTotalFees(row);
  let deadlineText = '-';
  let isOverdue = false;
  if (row.hasBeenPosted) {
    deadlineText = 'Verification';
  } else if (row.dateAccepted && row.promisedPostingTime) {
    const acceptedDate = new Date(row.dateAccepted);
    const deadline = new Date(acceptedDate.getTime() + row.promisedPostingTime * 60 * 1000);
    isOverdue = new Date() > deadline;
    deadlineText = deadline.toLocaleString('en-US', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  }
  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors" onClick={() => onView(row)}>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">#{row.transferBondTransactionID}</td>
      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white max-w-[200px] truncate">{defendantName}</td>
      {canViewRequestingAgency && <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-[180px] truncate">{row.requestingAgencyName || '-'}</td>}
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-[150px] truncate">{location}</td>
      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{formatCurrency(totalFees)}</td>
      <td className="px-4 py-3 text-sm">
        {row.hasBeenPosted ? (
          <span className="italic text-gray-600 dark:text-gray-400">Verification Pending</span>
        ) : (
          <span className={isOverdue ? 'text-error font-medium' : 'text-gray-600 dark:text-gray-400'}>
            Post by: {deadlineText} {isOverdue && <span className="text-error">(Overdue)</span>}
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-center">{row.numberOfBonds || '-'}</td>
      <td className="px-4 py-3 text-sm">
        <div className="flex items-center gap-1">
          <button onClick={(e) => { e.stopPropagation(); onView(row); }} className="p-1.5 text-primary hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors" title="View Details">
            <EyeIcon className="h-5 w-5" />
          </button>
          {!row.hasBeenPosted && (
            <button onClick={(e) => { e.stopPropagation(); onUpload(row); }} className="p-1.5 text-success hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors" title="Upload Powers">
              <ArrowUpTrayIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

const ClosedTableRow = ({ row, onView, canViewRequestingAgency }) => {
  const defendantName = `${row.defendantFirstName || ''} ${row.defendantLastName || ''}`.trim() || '-';
  const location = [row.county, row.state].filter(Boolean).join(', ') || '-';
  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors" onClick={() => onView(row)}>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">#{row.transferBondTransactionID}</td>
      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white max-w-[200px] truncate">{defendantName}</td>
      {canViewRequestingAgency && <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-[180px] truncate">{row.requestingAgencyName || '-'}</td>}
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{formatDateTime(row.createdAt)}</td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 italic">{row.isOnline ? 'Online' : 'Bankers In-House'}</td>
      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{formatCurrency(row.totalBondAmount)}</td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-[150px] truncate">{location}</td>
      <td className="px-4 py-3 text-sm">
        <button onClick={(e) => { e.stopPropagation(); onView(row); }} className="p-1.5 text-primary hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors" title="View Details">
          <EyeIcon className="h-5 w-5" />
        </button>
      </td>
    </tr>
  );
};

const CancelledTableRow = ({ row, onView, canViewRequestingAgency }) => {
  const defendantName = `${row.defendantFirstName || ''} ${row.defendantLastName || ''}`.trim() || '-';
  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors" onClick={() => onView(row)}>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">#{row.transferBondTransactionID}</td>
      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white max-w-[200px] truncate">{defendantName}</td>
      {canViewRequestingAgency && <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-[180px] truncate">{row.requestingAgencyName || '-'}</td>}
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-[200px] truncate">{row.cancelledReason || '-'}</td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{formatDateTime(row.cancelledDate)}</td>
      <td className="px-4 py-3 text-sm">
        <button onClick={(e) => { e.stopPropagation(); onView(row); }} className="p-1.5 text-primary hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors" title="View Details">
          <EyeIcon className="h-5 w-5" />
        </button>
      </td>
    </tr>
  );
};

// ──────────────────────────────────────────────────────────────────────
// Main Component
// ──────────────────────────────────────────────────────────────────────

const PostingList = ({ agencyId }) => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canViewRequestingAgency = can('READ:TRANSFER-BOND-REQUESTING-AGENCY');
  const isMobile = useIsMobile();

  // Tab state (matches legacy 3-tab structure) — restore from session on mount
  const [mainTab, setMainTab] = useState(() => sessionStorage.getItem('tb-posting-mainTab') || 'available');
  const [inProgressSubTab, setInProgressSubTab] = useState(() => sessionStorage.getItem('tb-posting-subTab') || 'accepted');
  const [quotedSubTab, setQuotedSubTab] = useState('pending');

  // Loading states
  const [loading, setLoading] = useState(false);
  const [closedLoading, setClosedLoading] = useState(false);
  const [cancelledLoading, setCancelledLoading] = useState(false);

  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  // Sorting
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');

  // Data states
  const [allOpenRequests, setAllOpenRequests] = useState([]);
  const [preferredOpenRequests, setPreferredOpenRequests] = useState([]);
  const [preferredOnly, setPreferredOnly] = useState(false);
  const [quotedRequests, setQuotedRequests] = useState([]);
  const [declinedQuotes, setDeclinedQuotes] = useState([]);
  const [acceptedRequests, setAcceptedRequests] = useState([]);
  const [closedRequests, setClosedRequests] = useState([]);
  const [cancelledRequests, setCancelledRequests] = useState([]);
  const [showAllCancelled, setShowAllCancelled] = useState(false);

  // Reference # filters (client-side, matches legacy)
  const [closedSearchRef, setClosedSearchRef] = useState('');
  const [cancelledSearchRef, setCancelledSearchRef] = useState('');

  // Cancelled date range filter (client-side, only when showAllCancelled)
  const [cancelledStartDate, setCancelledStartDate] = useState('');
  const [cancelledEndDate, setCancelledEndDate] = useState('');

  // Countdown tick for pending quotes (forces re-render every second)
  const [, setTick] = useState(0);

  // Closed transactions date range (default: last 30 days)
  const [closedStartDate, setClosedStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [closedEndDate, setClosedEndDate] = useState(() => new Date().toISOString().slice(0, 10));

  // ── Fetch functions ──────────────────────────────────────────────

  const fetchOpenRequests = useCallback(async (silent = false) => {
    if (!agencyId) return;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const response = await getOpenRequestsForAgency(agencyId);
      if (response.success) {
        const raw = response.data || {};
        if (Array.isArray(raw)) {
          setAllOpenRequests(raw);
          setPreferredOpenRequests([]);
        } else {
          setAllOpenRequests(raw.allRequests || []);
          setPreferredOpenRequests(raw.preferredRequests || []);
        }
      } else if (!silent) {
        setError(response.error || 'Failed to load open requests');
      }
    } catch {
      if (!silent) setError('Failed to load open requests');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [agencyId]);

  const fetchQuotedRequests = useCallback(async () => {
    if (!agencyId) return;
    try {
      const response = await getQuotedRequestsForAgency(agencyId);
      if (response.success) {
        const allQuoted = response.data || [];
        // Split into pending and declined (matches legacy postingQuotes.jsx)
        setQuotedRequests(allQuoted.filter((q) => !q.hasBeenDeclined && q.statusCd === 'O'));
        setDeclinedQuotes(allQuoted.filter((q) => q.hasBeenDeclined && q.userCleared !== true));
      }
    } catch {
      // Silently fail
    }
  }, [agencyId]);

  const fetchAcceptedRequests = useCallback(async () => {
    if (!agencyId) return;
    try {
      const response = await getAcceptedRequestsForAgency(agencyId);
      if (response.success) {
        setAcceptedRequests(response.data || []);
      }
    } catch {
      // Silently fail
    }
  }, [agencyId]);

  const fetchClosedRequests = useCallback(async () => {
    if (!agencyId) return;
    // Validate date range (max 90 days)
    const start = new Date(closedStartDate);
    const end = new Date(closedEndDate);
    const diffDays = (end - start) / (1000 * 60 * 60 * 24);
    if (diffDays > 90) {
      setError('Date range should not exceed 90 days.');
      return;
    }
    if (start > end) {
      setError('Start date cannot be after end date.');
      return;
    }
    setClosedLoading(true);
    setError(null);
    try {
      const response = await getClosedRequestsForAgency(agencyId, closedStartDate, closedEndDate);
      if (response.success) {
        setClosedRequests(response.data || []);
      } else {
        setError(response.error || 'Failed to load closed transactions');
      }
    } catch {
      setError('Failed to load closed transactions');
    } finally {
      setClosedLoading(false);
    }
  }, [agencyId, closedStartDate, closedEndDate]);

  const fetchCancelledRequests = useCallback(async () => {
    if (!agencyId) return;
    setCancelledLoading(true);
    try {
      const response = await getCancelledRequestsForAgency(agencyId, showAllCancelled);
      if (response.success) {
        setCancelledRequests(response.data || []);
      }
    } catch {
      // Silently fail
    } finally {
      setCancelledLoading(false);
    }
  }, [agencyId, showAllCancelled]);

  // Fetch main data on mount
  useEffect(() => {
    fetchOpenRequests();
    fetchQuotedRequests();
    fetchAcceptedRequests();
  }, [fetchOpenRequests, fetchQuotedRequests, fetchAcceptedRequests]);

  // Fetch cancelled separately (re-fetches when showAllCancelled toggles)
  useEffect(() => {
    fetchCancelledRequests();
  }, [fetchCancelledRequests]);

  // Auto-refresh every 10 seconds (matches legacy postingLandingpage.jsx)
  // Pass silent=true so loading skeletons don't flash on refresh
  useEffect(() => {
    const interval = setInterval(() => {
      fetchOpenRequests(true);
      fetchQuotedRequests();
      fetchAcceptedRequests();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchOpenRequests, fetchQuotedRequests, fetchAcceptedRequests]);

  // Countdown timer for pending quotes (updates every second, matches legacy)
  const activeView = (() => {
    if (mainTab === 'available') return 'open';
    if (mainTab === 'inProgress') return inProgressSubTab;
    if (mainTab === 'quotedDeclined') return quotedSubTab;
    return 'open';
  })();

  useEffect(() => {
    if (activeView !== 'pending') return;
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [activeView]);

  // ── Navigation handlers ──────────────────────────────────────────

  const handleView = (row) => {
    navigate(`/app/transfer-bonds/posting/${row.transferBondTransactionID}`);
  };

  const handleQuote = (row) => {
    navigate(`/app/transfer-bonds/posting/${row.transferBondTransactionID}?action=quote`);
  };

  const handleUpload = (row) => {
    navigate(`/app/transfer-bonds/posting/${row.transferBondTransactionID}?action=upload`);
  };

  // Cancel a pending quote (calls API directly, matches legacy postingQuotes.jsx)
  const handleCancelQuote = async (row) => {
    if (!window.confirm('Cancel this quote?')) return;
    try {
      const response = await cancelPendingRequest(row.transferBondQuoteID, agencyId);
      if (response.success) {
        toast.success('Quote cancelled successfully.');
        fetchQuotedRequests();
      } else {
        toast.error(response.error || 'Failed to cancel quote.');
        setError(response.error || 'Failed to cancel quote');
      }
    } catch {
      toast.error('Failed to cancel quote.');
      setError('Failed to cancel quote');
    }
  };

  // Clear a declined quote (calls API directly, matches legacy postingQuotes.jsx)
  const handleClearDeclined = async (row) => {
    try {
      const response = await clearDeclinedRequest(row.transferBondQuoteID, agencyId);
      if (response.success) {
        toast.success('Declined quote cleared.');
        fetchQuotedRequests();
      } else {
        toast.error(response.error || 'Failed to clear quote.');
        setError(response.error || 'Failed to clear quote');
      }
    } catch {
      toast.error('Failed to clear quote.');
      setError('Failed to clear quote');
    }
  };

  // ── Sorting ────────────────────────────────────────────────────

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setPage(1);
  };

  const sortRows = (rows) => {
    if (!sortField) return rows;
    return [...rows].sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'transferBondTransactionID':
          aVal = a.transferBondTransactionID || 0;
          bVal = b.transferBondTransactionID || 0;
          break;
        case 'defendant':
          aVal = `${a.defendantFirstName || ''} ${a.defendantLastName || ''}`.toLowerCase();
          bVal = `${b.defendantFirstName || ''} ${b.defendantLastName || ''}`.toLowerCase();
          break;
        case 'location':
          aVal = `${a.county || ''} ${a.state || ''}`.toLowerCase();
          bVal = `${b.county || ''} ${b.state || ''}`.toLowerCase();
          break;
        case 'venue':
          aVal = `${a.jailLocation || ''} ${a.courtPlace || ''}`.toLowerCase();
          bVal = `${b.jailLocation || ''} ${b.courtPlace || ''}`.toLowerCase();
          break;
        case 'numberOfBonds':
          aVal = a.numberOfBonds || 0;
          bVal = b.numberOfBonds || 0;
          break;
        case 'createdAt':
          aVal = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bVal = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          break;
        case 'totalFees':
          aVal = calculateTotalFees(a);
          bVal = calculateTotalFees(b);
          break;
        case 'promisedPostingTime':
          aVal = a.promisedPostingTimeInMinutes || a.promisedPostingTime || 0;
          bVal = b.promisedPostingTimeInMinutes || b.promisedPostingTime || 0;
          break;
        case 'expireDate':
          aVal = a.expireDate ? new Date(a.expireDate).getTime() : 0;
          bVal = b.expireDate ? new Date(b.expireDate).getTime() : 0;
          break;
        case 'deadline':
          if (a.hasBeenPosted) aVal = Number.MAX_SAFE_INTEGER;
          else if (a.dateAccepted && a.promisedPostingTime) {
            aVal = new Date(a.dateAccepted).getTime() + a.promisedPostingTime * 60 * 1000;
          } else aVal = 0;
          if (b.hasBeenPosted) bVal = Number.MAX_SAFE_INTEGER;
          else if (b.dateAccepted && b.promisedPostingTime) {
            bVal = new Date(b.dateAccepted).getTime() + b.promisedPostingTime * 60 * 1000;
          } else bVal = 0;
          break;
        case 'totalBondAmount':
          aVal = a.totalBondAmount || 0;
          bVal = b.totalBondAmount || 0;
          break;
        case 'cancelledDate':
          aVal = a.cancelledDate ? new Date(a.cancelledDate).getTime() : 0;
          bVal = b.cancelledDate ? new Date(b.cancelledDate).getTime() : 0;
          break;
        case 'dateRecorded':
          aVal = a.dateRecorded ? new Date(a.dateRecorded).getTime() : 0;
          bVal = b.dateRecorded ? new Date(b.dateRecorded).getTime() : 0;
          break;
        default:
          aVal = a[sortField];
          bVal = b[sortField];
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // ── Active view data ─────────────────────────────────────────────

  const getCurrentData = () => {
    switch (activeView) {
      case 'open':
        return preferredOnly ? preferredOpenRequests : allOpenRequests;
      case 'pending':
        return quotedRequests;
      case 'declined':
        return declinedQuotes;
      case 'accepted':
        return acceptedRequests;
      case 'closed': {
        let data = closedRequests;
        if (closedSearchRef.trim()) {
          data = data.filter((r) =>
            String(r.transferBondTransactionID).includes(closedSearchRef.trim())
          );
        }
        return data;
      }
      case 'cancelled': {
        let data = cancelledRequests;
        if (cancelledSearchRef.trim()) {
          data = data.filter((r) =>
            String(r.transferBondTransactionID).includes(cancelledSearchRef.trim())
          );
        }
        if (showAllCancelled && cancelledStartDate && cancelledEndDate) {
          const start = new Date(cancelledStartDate);
          const end = new Date(cancelledEndDate);
          end.setHours(23, 59, 59, 999);
          data = data.filter((r) => {
            if (!r.cancelledDate) return false;
            const d = new Date(r.cancelledDate);
            return d >= start && d <= end;
          });
        }
        return data;
      }
      default:
        return [];
    }
  };

  const isCurrentLoading = () => {
    if (activeView === 'closed') return closedLoading;
    if (activeView === 'cancelled') return cancelledLoading;
    return loading;
  };

  const rows = getCurrentData();

  // Filter by search term
  const filteredRows = searchTerm
    ? rows.filter((row) => {
        const s = searchTerm.toLowerCase();
        return (
          (row.defendantFirstName || '').toLowerCase().includes(s) ||
          (row.defendantLastName || '').toLowerCase().includes(s) ||
          (row.county || '').toLowerCase().includes(s) ||
          (row.state || '').toLowerCase().includes(s) ||
          (row.requestingAgencyName || '').toLowerCase().includes(s) ||
          String(row.transferBondTransactionID).includes(searchTerm)
        );
      })
    : rows;

  const sortedRows = sortRows(filteredRows);
  const totalPages = Math.ceil(sortedRows.length / pageSize);
  const paginatedRows = sortedRows.slice((page - 1) * pageSize, page * pageSize);

  const getRowId = (row) => {
    if (activeView === 'pending' || activeView === 'declined') {
      return `${row.transferBondTransactionID}-${row.transferBondQuoteID || ''}`;
    }
    return row.transferBondTransactionID;
  };

  // ── Tab definitions ────────────────────────────────────────────

  const mainTabs = [
    { id: 'available', label: 'Available to Quote', icon: InboxIcon, count: preferredOnly ? preferredOpenRequests.length : allOpenRequests.length, color: 'bg-blue-500' },
    { id: 'inProgress', label: 'In-Progress Transfers', icon: ArrowPathIcon, count: acceptedRequests.length, color: 'bg-green-500' },
    { id: 'quotedDeclined', label: 'Quoted & Declined', icon: ChatBubbleLeftEllipsisIcon, count: quotedRequests.length + declinedQuotes.length, color: 'bg-yellow-500' },
  ];

  const inProgressSubTabs = [
    { id: 'accepted', label: 'Accepted', count: acceptedRequests.length },
    { id: 'closed', label: 'Closed', count: closedRequests.length },
    { id: 'cancelled', label: 'Cancelled', count: cancelledRequests.length },
  ];

  const quotedSubTabs = [
    { id: 'pending', label: 'Pending Quotes', count: quotedRequests.length },
    { id: 'declined', label: 'Declined & Expired', count: declinedQuotes.length },
  ];

  const getEmptyMessage = () => {
    switch (activeView) {
      case 'open': return preferredOnly ? 'No requests in preferred counties' : 'No open requests in your service area';
      case 'pending': return 'No pending quotes';
      case 'declined': return 'No declined or expired quotes';
      case 'accepted': return 'No accepted requests';
      case 'closed': return 'No closed transactions found. Use the date range above to search.';
      case 'cancelled': return 'No cancelled transactions';
      default: return 'No data';
    }
  };

  // ── Render helpers ─────────────────────────────────────────────

  const renderTableHeaders = () => {
    switch (activeView) {
      case 'open':
        return (
          <tr>
            <SortableHeader label="Ref" field="transferBondTransactionID" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            <SortableHeader label="Defendant" field="defendant" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            {canViewRequestingAgency && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Requesting Agency</th>}
            <SortableHeader label="Location" field="location" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            <SortableHeader label="Venue" field="venue" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            <SortableHeader label="Bonds" field="numberOfBonds" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} align="center" />
            <SortableHeader label="Created" field="createdAt" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Actions</th>
          </tr>
        );
      case 'pending':
        return (
          <tr>
            <SortableHeader label="Ref" field="transferBondTransactionID" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            <SortableHeader label="Defendant" field="defendant" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            {canViewRequestingAgency && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Requesting Agency</th>}
            <SortableHeader label="Location" field="location" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Jail</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Courthouse</th>
            <SortableHeader label="Bonds" field="numberOfBonds" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} align="center" />
            <SortableHeader label="Fees" field="totalFees" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            <SortableHeader label="Post Time" field="promisedPostingTime" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} align="center" />
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Status</th>
            <SortableHeader label="Expires" field="expireDate" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Actions</th>
          </tr>
        );
      case 'declined':
        return (
          <tr>
            <SortableHeader label="Ref" field="transferBondTransactionID" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            <SortableHeader label="Defendant" field="defendant" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            {canViewRequestingAgency && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Requesting Agency</th>}
            <SortableHeader label="Location" field="location" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Decline Reason</th>
            <SortableHeader label="Fees" field="totalFees" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            <SortableHeader label="Date Given" field="dateRecorded" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Actions</th>
          </tr>
        );
      case 'accepted':
        return (
          <tr>
            <SortableHeader label="Ref" field="transferBondTransactionID" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            <SortableHeader label="Defendant" field="defendant" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            {canViewRequestingAgency && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Requesting Agency</th>}
            <SortableHeader label="Location" field="location" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            <SortableHeader label="Fees" field="totalFees" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            <SortableHeader label="Status" field="deadline" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            <SortableHeader label="Bonds" field="numberOfBonds" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} align="center" />
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Actions</th>
          </tr>
        );
      case 'closed':
        return (
          <tr>
            <SortableHeader label="Ref" field="transferBondTransactionID" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            <SortableHeader label="Defendant" field="defendant" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            {canViewRequestingAgency && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Requesting Agency</th>}
            <SortableHeader label="Created" field="createdAt" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Origin</th>
            <SortableHeader label="Bond Amount" field="totalBondAmount" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            <SortableHeader label="Location" field="location" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Actions</th>
          </tr>
        );
      case 'cancelled':
        return (
          <tr>
            <SortableHeader label="Ref" field="transferBondTransactionID" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            <SortableHeader label="Defendant" field="defendant" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            {canViewRequestingAgency && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Requesting Agency</th>}
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Reason</th>
            <SortableHeader label="Date Cancelled" field="cancelledDate" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Actions</th>
          </tr>
        );
      default:
        return null;
    }
  };

  const renderTableRows = () => {
    switch (activeView) {
      case 'open':
        return paginatedRows.map((row) => <OpenTableRow key={getRowId(row)} row={row} onView={handleView} onQuote={handleQuote} canViewRequestingAgency={canViewRequestingAgency} />);
      case 'pending':
        return paginatedRows.map((row) => <QuotedTableRow key={getRowId(row)} row={row} onView={handleView} onCancel={handleCancelQuote} canViewRequestingAgency={canViewRequestingAgency} />);
      case 'declined':
        return paginatedRows.map((row) => <DeclinedTableRow key={getRowId(row)} row={row} onView={handleView} onClear={handleClearDeclined} canViewRequestingAgency={canViewRequestingAgency} />);
      case 'accepted':
        return paginatedRows.map((row) => <AcceptedTableRow key={getRowId(row)} row={row} onView={handleView} onUpload={handleUpload} canViewRequestingAgency={canViewRequestingAgency} />);
      case 'closed':
        return paginatedRows.map((row) => <ClosedTableRow key={getRowId(row)} row={row} onView={handleView} canViewRequestingAgency={canViewRequestingAgency} />);
      case 'cancelled':
        return paginatedRows.map((row) => <CancelledTableRow key={getRowId(row)} row={row} onView={handleView} canViewRequestingAgency={canViewRequestingAgency} />);
      default:
        return null;
    }
  };

  const renderMobileCards = () => {
    return sortedRows.map((row) => {
      const key = getRowId(row);
      switch (activeView) {
        case 'open':
          return <OpenRequestCard key={key} row={row} onView={handleView} onQuote={handleQuote} canViewRequestingAgency={canViewRequestingAgency} />;
        case 'pending':
          return <QuotedRequestCard key={key} row={row} onView={handleView} onCancel={handleCancelQuote} canViewRequestingAgency={canViewRequestingAgency} />;
        case 'declined':
          return <DeclinedQuoteCard key={key} row={row} onView={handleView} onClear={handleClearDeclined} canViewRequestingAgency={canViewRequestingAgency} />;
        case 'accepted':
          return <AcceptedRequestCard key={key} row={row} onView={handleView} onUpload={handleUpload} canViewRequestingAgency={canViewRequestingAgency} />;
        case 'closed':
          return <ClosedRequestCard key={key} row={row} onView={handleView} canViewRequestingAgency={canViewRequestingAgency} />;
        case 'cancelled':
          return <CancelledRequestCard key={key} row={row} onView={handleView} canViewRequestingAgency={canViewRequestingAgency} />;
        default:
          return null;
      }
    });
  };

  // ── JSX ────────────────────────────────────────────────────────

  return (
    <div>
      {/* Main tabs */}
      <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-1 mb-4">
        <div className="flex">
          {mainTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setMainTab(tab.id); sessionStorage.setItem('tb-posting-mainTab', tab.id); setPage(1); setSearchTerm(''); }}
              className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-2.5 px-1 sm:px-2 text-sm font-medium rounded-md transition-all ${
                mainTab === tab.id
                  ? 'bg-warning text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
              title={tab.label}
            >
              <tab.icon className="h-4 w-4 sm:hidden flex-shrink-0" />
              <span className="hidden sm:inline truncate">{tab.label}</span>
              {tab.count > 0 && (
                <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-medium rounded-full text-white ${
                  mainTab === tab.id ? 'bg-white/20' : tab.color
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Sub-tabs for In-Progress Transfers */}
      {mainTab === 'inProgress' && (
        <div className="flex gap-1 mb-4 border-b border-gray-200 dark:border-gray-700">
          {inProgressSubTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setInProgressSubTab(tab.id); sessionStorage.setItem('tb-posting-subTab', tab.id); setPage(1); setSearchTerm(''); }}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                inProgressSubTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {tab.label}
              {tab.count > 0 && <span className="ml-1.5 text-xs text-gray-400 dark:text-gray-500">({tab.count})</span>}
            </button>
          ))}
        </div>
      )}

      {/* Sub-tabs for Quoted & Declined */}
      {mainTab === 'quotedDeclined' && (
        <div className="flex gap-1 mb-4 border-b border-gray-200 dark:border-gray-700">
          {quotedSubTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setQuotedSubTab(tab.id); setPage(1); setSearchTerm(''); }}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                quotedSubTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {tab.label}
              {tab.count > 0 && <span className="ml-1.5 text-xs text-gray-400 dark:text-gray-500">({tab.count})</span>}
            </button>
          ))}
        </div>
      )}

      {/* Preferred counties toggle (Available to Quote only) */}
      {mainTab === 'available' && (
        <div className="flex items-center gap-3 mb-4">
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={preferredOnly}
              onChange={(e) => { setPreferredOnly(e.target.checked); setPage(1); }}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-gray-300 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-gray-600 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary" />
          </label>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {preferredOnly ? 'Showing Preferred Counties Only' : 'Showing All Requests'}
          </span>
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            ({(preferredOnly ? preferredOpenRequests : allOpenRequests).length})
          </span>
        </div>
      )}

      {/* Closed transactions: date range controls + reference filter */}
      {activeView === 'closed' && (
        <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">Search closed transactions within a 90-day date range. Use the reference filter to narrow displayed results.</p>
          <div className="flex flex-wrap gap-3 items-end mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Start Date</label>
              <input
                type="date"
                value={closedStartDate}
                onChange={(e) => setClosedStartDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">End Date</label>
              <input
                type="date"
                value={closedEndDate}
                onChange={(e) => setClosedEndDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>
            <button
              onClick={fetchClosedRequests}
              disabled={closedLoading}
              className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {closedLoading ? 'Searching...' : 'Search'}
            </button>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Filter by Reference #</label>
            <input
              type="text"
              value={closedSearchRef}
              onChange={(e) => { setClosedSearchRef(e.target.value); setPage(1); }}
              placeholder="Type to filter displayed results..."
              className="w-full md:max-w-xs px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary placeholder:text-gray-400 dark:placeholder:text-gray-500"
            />
          </div>
        </div>
      )}

      {/* Cancelled transactions: recent/all toggle + filters */}
      {activeView === 'cancelled' && (
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={showAllCancelled}
                onChange={(e) => {
                  setShowAllCancelled(e.target.checked);
                  setCancelledSearchRef('');
                  setCancelledStartDate('');
                  setCancelledEndDate('');
                }}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-gray-300 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-gray-600 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary" />
            </label>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {showAllCancelled ? 'Viewing All Cancelled Transactions' : 'Viewing Recent Cancelled Transactions'}
            </span>
          </div>
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Filter by Reference #</label>
              <input
                type="text"
                value={cancelledSearchRef}
                onChange={(e) => { setCancelledSearchRef(e.target.value); setPage(1); }}
                placeholder="Type to filter..."
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary placeholder:text-gray-400 dark:placeholder:text-gray-500"
              />
            </div>
            {showAllCancelled && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={cancelledStartDate}
                    onChange={(e) => { setCancelledStartDate(e.target.value); setPage(1); }}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">End Date</label>
                  <input
                    type="date"
                    value={cancelledEndDate}
                    onChange={(e) => { setCancelledEndDate(e.target.value); setPage(1); }}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Search bar */}
      <div className="mb-4 relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search by defendant, county..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
          className="w-full md:max-w-xs pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary transition-colors placeholder:text-gray-400 dark:placeholder:text-gray-500"
        />
      </div>

      {/* Error alert */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Content */}
      {isMobile ? (
        isCurrentLoading() ? <MobileLoadingSkeleton /> : sortedRows.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-gray-500 dark:text-gray-400">{getEmptyMessage()}</p>
          </div>
        ) : (
          <div>
            {renderMobileCards()}
            <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-4">
              Showing {sortedRows.length} of {rows.length} requests
            </p>
          </div>
        )
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {isCurrentLoading() ? (
            <div className="p-4"><TableLoadingSkeleton /></div>
          ) : sortedRows.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-gray-500 dark:text-gray-400">{getEmptyMessage()}</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                    {renderTableHeaders()}
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {renderTableRows()}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, sortedRows.length)} of {sortedRows.length}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="p-2 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeftIcon className="h-4 w-4" />
                    </button>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Page {page} of {totalPages}</span>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="p-2 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRightIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PostingList;
